import { useState, useEffect } from "react";

export type Breakpoint = "mobile" | "tablet" | "desktop";

/**
 * Hook to detect current breakpoint
 * - mobile: < 768px
 * - tablet: 768px - 1024px
 * - desktop: > 1024px
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window === "undefined") return "desktop";
    const width = window.innerWidth;
    if (width < 768) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setBreakpoint("mobile");
      } else if (width < 1024) {
        setBreakpoint("tablet");
      } else {
        setBreakpoint("desktop");
      }
    };

    // Initial check
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return breakpoint;
}

/**
 * Hook to check if current breakpoint matches
 */
export function useIsBreakpoint(target: Breakpoint): boolean {
  const breakpoint = useBreakpoint();
  return breakpoint === target;
}

/**
 * Hook to check if mobile
 */
export function useIsMobile(): boolean {
  return useIsBreakpoint("mobile");
}

/**
 * Hook to check if tablet
 */
export function useIsTablet(): boolean {
  return useIsBreakpoint("tablet");
}

/**
 * Hook to check if desktop
 */
export function useIsDesktop(): boolean {
  return useIsBreakpoint("desktop");
}

